import React, { ElementType, FC, useEffect, useState } from 'react';
import { Divider, Typography, useTheme } from '@material-ui/core';
import trimLevelNameStyles from './trimLevelNameStyles';
import clsx from 'clsx';
import useSelectedTrimLevelName from '../../../../trimLevels/hooks/useSelectedTrimLevelName';
import stringUtilities from '../../../../../../common/utilities/stringUtilities';
import { ISpcTheme } from '../../../theme/ISpcTheme';
import { useTrimLevel } from '../../../../trimLevels/contexts/TrimLevelContext';
import usePlatformConfig from '../../../react/usePlatformConfig';

export interface ITrimLevelNameProps {
  name: string;
  type?: ElementType<any>;
  dataTest?: string;
  showDivider?: boolean;
  section?: string;
  classes?: {
    variantNameText?: string;
  };
}

const TrimLevelName: FC<ITrimLevelNameProps> = ({ name, type, dataTest, showDivider, section, classes }) => {
  const styles = trimLevelNameStyles();
  let className = '';
  const {
    spc: {
      trimLevels: { colouredModelName, showMarketingPrefix },
    },
  } = useTheme<ISpcTheme>();
  const {
    platformConfig: { hideVariantName },
  } = usePlatformConfig();

  const { trimName: trimLevelName } = useTrimLevel();
  const { trimName: selectedTrimLevelName } = useSelectedTrimLevelName(showMarketingPrefix);
  const displayedTrimName = section === 'banner' ? trimLevelName : selectedTrimLevelName;
  const [sanitizedVariantName, setSanitizedVariantName] = useState<string>('');

  useEffect(() => {
    if (colouredModelName) {
      setSanitizedVariantName(stringUtilities.sanitizeVariantName(displayedTrimName, name));
    } else {
      setSanitizedVariantName(name);
    }
  }, [displayedTrimName, colouredModelName, name]);

  if (section) {
    if (type === 'h4') {
      className = section === 'summary' ? styles.primaryNameSummarySection : styles.primaryNameTitleSection;
    } else {
      className = section === 'summary' ? styles.secondaryNameSummarySection : styles.secondaryNameTitleSection;
    }
  }

  return (
    <>
      <Typography
        component={type}
        className={clsx([
          type === 'h4' ? styles.primaryName : styles.secondaryName,
          className,
          section === 'banner' && styles.trimLevelsPageTitle,
        ])}
        data-test={dataTest}
      >
        {displayedTrimName && colouredModelName && (
          <>
            <span className={clsx({ [styles.modelNameText]: colouredModelName })} data-test="title:variant">
              {displayedTrimName}
            </span>
            &nbsp;
          </>
        )}
        {!hideVariantName && (
          <Typography
            component="span"
            className={clsx(styles.variantNameText, classes?.variantNameText)}
            data-test="title:variantName"
          >
            {sanitizedVariantName}
          </Typography>
        )}
      </Typography>
      {showDivider && <Divider orientation="vertical" className={styles.divider} />}
    </>
  );
};

export default TrimLevelName;
